import { useEffect, useMemo, useState } from "react";
import {
  Trophy, Medal, CheckCircle, Heart, Flame, Newspaper, Crown, Quotes,
  ArrowUpRight, Barbell, Users, CurrencyDollar, Clock, Fire, CaretDown,
} from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import FlagStripe from "@/components/site/FlagStripe";
import PressCard from "@/components/site/PressCard";
import PressVideoFeature from "@/components/site/PressVideoFeature";
import { PRESS, FEATURED_VIDEO } from "@/data/press";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const num = (n) => Number(n || 0).toLocaleString();
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/* ----------------------------- Section header ---------------------------- */
const SectionLabel = ({ children }) => (
  <p className="text-[#B32027] uppercase tracking-[0.45em] text-[11px] font-bold mb-4">/ {children}</p>
);

/* -------------------------------- Stat block ------------------------------ */
const Stat = ({ icon, value, label, sub, accent }) => (
  <div className={`border p-6 md:p-7 ${accent ? "border-[#B32027]/60 bg-[#B32027]/[0.06]" : "border-white/12 bg-[#0F0F0F]"}`}>
    <div className={`mb-3 ${accent ? "text-[#B32027]" : "text-white/60"}`}>{icon}</div>
    <p className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-none">{value}</p>
    <p className="text-white/55 text-[11px] uppercase tracking-[0.22em] font-bold mt-2">{label}</p>
    {sub && <p className="text-white/40 text-xs mt-1.5">{sub}</p>}
  </div>
);

/* ------------------------------ Leaderboard row --------------------------- */
const LbRow = ({ e, rank }) => (
  <li
    data-testid={`legacy-lb-row-${rank}`}
    className={`px-4 sm:px-6 py-3.5 flex items-center gap-4 ${
      e.pinned ? "bg-[#FFC72C]/[0.06]" : e.finished ? "bg-emerald-500/[0.04]" : ""
    }`}
  >
    <div className="w-8 flex items-center justify-center shrink-0">
      {e.pinned ? <span className="text-xl" role="img" aria-label="owner">💪</span>
        : e.finished ? <CheckCircle size={20} weight="fill" className="text-emerald-400" />
        : rank <= 3 ? <Crown size={18} weight="fill" className={rank === 1 ? "text-yellow-400" : rank === 2 ? "text-slate-300" : "text-amber-600"} />
        : <span className="text-white/40 font-bold text-sm">#{rank}</span>}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="font-display font-black tracking-tighter text-lg sm:text-xl truncate">{e.display_name}</p>
        {e.finished && (
          <span className="inline-flex items-center gap-1 text-[8px] uppercase tracking-[0.2em] font-bold text-emerald-300 border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 whitespace-nowrap">
            911 finisher
          </span>
        )}
      </div>
      {e.moving_for && <p className="text-white/45 text-xs truncate">Moving for {e.moving_for}</p>}
    </div>
    <div className="text-right shrink-0">
      <p className={`font-display font-black tracking-tighter text-xl sm:text-2xl tabular-nums ${
        e.pinned ? "text-[#FFC72C]" : e.finished ? "text-emerald-400" : "text-[#B32027]"
      }`}>{num(e.minutes)}<span className="text-white/40 text-xs">m</span></p>
    </div>
  </li>
);

/* ------------------------------ Highlight item ---------------------------- */
const Highlight = ({ date, title, body }) => (
  <div className="relative pl-8 pb-8 border-l border-white/15 last:border-transparent last:pb-0">
    <span className="absolute -left-[7px] top-1 w-3.5 h-3.5 rounded-full bg-[#B32027] ring-4 ring-[#0A0A0A]" />
    {date && <p className="text-[#B32027] text-[10px] uppercase tracking-[0.3em] font-bold mb-1">{date}</p>}
    <h3 className="font-display font-black uppercase tracking-tight text-lg md:text-xl">{title}</h3>
    {body && <p className="text-white/60 text-sm mt-1 leading-relaxed max-w-xl">{body}</p>}
  </div>
);

export default function LegacyChallenge() {
  const [stats, setStats] = useState(null);
  const [lb, setLb] = useState(null);
  const [wall, setWall] = useState(null);
  const [showAllLb, setShowAllLb] = useState(false);
  const [email, setEmail] = useState("");
  const [signupMsg, setSignupMsg] = useState("");

  useEffect(() => {
    document.title = "Sweat With Slay Legacy Challenge · Year 1 — 2026";
    Promise.all([
      fetch(`${BACKEND}/api/sacrifice/live-stats`).then((r) => r.ok ? r.json() : null).catch(() => null),
      fetch(`${BACKEND}/api/sacrifice/leaderboard`).then((r) => r.ok ? r.json() : null).catch(() => null),
      fetch(`${BACKEND}/api/sacrifice/legacy-wall`).then((r) => r.ok ? r.json() : null).catch(() => null),
    ]).then(([s, l, w]) => { setStats(s); setLb(l); setWall(w); });
  }, []);

  const entries = lb?.entries || [];
  const pinned = entries.filter((e) => e.pinned);
  const finishers = entries.filter((e) => !e.pinned && e.finished);
  const working = entries.filter((e) => !e.pinned && !e.finished);
  const orderedWorking = showAllLb ? working : working.slice(0, 10);
  const honorees = wall?.honorees || [];

  const totalMinutes = stats?.total_minutes ?? lb?.total_minutes ?? 0;
  const participants = stats?.participants ?? lb?.participants ?? 0;
  const finisherCount = stats?.finishers ?? lb?.finishers ?? 0;
  const totalRaised = stats?.total_raised ?? 0;
  const hours = Math.round(totalMinutes / 60);

  const joinYear2 = async (e) => {
    e.preventDefault();
    setSignupMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/sacrifice/year2-waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: "" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Try again");
      setSignupMsg(
        j.already_on_list
          ? "You're already on the list — see you for Year 2."
          : "You're in. Check your inbox — we just sent your confirmation."
      );
      setEmail("");
    } catch (err) {
      setSignupMsg(err?.message || "Hmm, that didn't go through. Try again?");
    }
  };

  return (
    <main data-testid="legacy-challenge-page" className="bg-[#0A0A0A] text-white font-body min-h-screen pt-20">
      <Nav />
      <FlagStripe position="top" />

      {/* ============================= HERO ============================= */}
      <header className="relative px-5 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 overflow-hidden">
        <p className="absolute -top-6 right-0 font-display font-black uppercase tracking-tighter text-white/[0.03] text-[200px] md:text-[340px] leading-none pointer-events-none select-none">911</p>
        <div className="relative max-w-5xl">
          <div className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 px-3 py-1.5 mb-6">
            <CheckCircle size={13} weight="fill" className="text-emerald-400" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-300" data-testid="legacy-year-badge">Year 1 · 2026 · Complete</span>
          </div>
          <p className="text-[#B32027] uppercase tracking-[0.4em] text-xs font-bold mb-4">Sweat With Slay Legacy Challenge</p>
          <h1 className="font-display font-black uppercase leading-[0.86] tracking-tighter text-[2.7rem] sm:text-7xl lg:text-8xl mb-7">
            Year 1:<br /><span className="text-[#B32027]">Mission accomplished.</span>
          </h1>
          <div className="text-white/80 text-lg md:text-xl leading-relaxed max-w-2xl space-y-1">
            <p>What started as a challenge became a community.</p>
            <p>We moved. We competed. We raised money.</p>
            <p>We honored people who mattered to us. We showed up for each other.</p>
            <p className="text-white font-bold">And we made Year 1 count.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-12" data-testid="legacy-hero-stats">
            <Stat accent icon={<Users size={22} weight="fill" />} value={num(participants)} label="Participants" sub="moving as one" />
            <Stat icon={<Clock size={22} weight="fill" />} value={num(totalMinutes)} label="Minutes moved" sub={`${num(hours)} hours of movement`} />
            <Stat icon={<Fire size={22} weight="fill" />} value={num(finisherCount)} label="911 finishers" sub="completed all 911" />
            <Stat icon={<CurrencyDollar size={22} weight="fill" />} value={money(totalRaised)} label="Raised" sub="for the families" />
          </div>
        </div>
      </header>

      {/* ========================= BY THE NUMBERS ======================== */}
      <section data-testid="legacy-by-the-numbers" className="px-5 md:px-12 py-16 md:py-20 border-t border-white/10 bg-[#0C0C0C]">
        <div className="max-w-5xl">
          <SectionLabel>Year 1 by the numbers</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-10">Look what we did.</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={<Barbell size={22} weight="fill" />} value={num(participants)} label="People who showed up" />
            <Stat icon={<Clock size={22} weight="fill" />} value={num(hours)} label="Hours in motion" sub={`${num(totalMinutes)} total minutes`} />
            <Stat icon={<Trophy size={22} weight="fill" />} value={num(finisherCount)} label="Hit the full 911" />
            <Stat accent icon={<Heart size={22} weight="fill" />} value={money(totalRaised)} label="Raised for the cause" sub="split 50/50 · T2T + FDNY" />
          </div>
          <p className="text-white/45 text-sm mt-6 max-w-2xl">
            Every number here is the <span className="text-white/70">final, frozen result</span> of Year 1 — pulled straight from the challenge records. Nothing live, nothing editable. This is history now.
          </p>
        </div>
      </section>

      {/* ============================ THE STORY ========================== */}
      <section data-testid="legacy-story" className="px-5 md:px-12 py-16 md:py-24 border-t border-white/10">
        <div className="max-w-3xl">
          <SectionLabel>The story</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">
            It was never just a workout.
          </h2>
          <div className="text-white/80 text-base md:text-lg leading-relaxed space-y-5">
            <p>Sweat for Sacrifice started with two names: <span className="text-white font-bold">Uncle Timmy Goss</span> and <span className="text-white font-bold">Uncle Harry D&rsquo;Onofrio</span> — a Marine firefighter and an NYPD officer who both answered the call on 9/11, and both paid for it with their lives.</p>
            <p>The idea was simple and stubborn: <span className="text-white">911 minutes of movement</span>, for the people we carry. Walk, run, lift, climb, stretch — it didn&rsquo;t matter how. What mattered was showing up, and dedicating every minute to someone who deserved to be remembered.</p>
            <p>It turned into something bigger than fitness. It became movement with meaning — competition that pulled people closer, sweat that turned into dollars for <span className="text-white font-bold">Tunnel to Towers</span> and the <span className="text-white font-bold">FDNY Fire Family Transport Foundation</span>, and a community that refused to forget.</p>
            <p className="text-white font-bold text-xl md:text-2xl font-display tracking-tight">We didn&rsquo;t sweat for ourselves. We moved for something bigger.</p>
          </div>
        </div>
      </section>

      {/* ======================= FINAL LEADERBOARD ======================= */}
      <section data-testid="legacy-final-leaderboard" className="px-5 md:px-12 py-16 md:py-20 border-t border-white/10 bg-[#0C0C0C]">
        <div className="max-w-4xl">
          <SectionLabel>Final Year 1 leaderboard</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-3">The standings, frozen for good.</h2>
          <p className="text-white/60 text-base mb-8 max-w-2xl">The leaderboard made it competitive. The community made it matter. These results are permanent.</p>

          {entries.length === 0 ? (
            <div className="border border-white/15 bg-[#0F0F0F] px-6 py-14 text-center text-white/45" data-testid="legacy-lb-empty">
              Final standings will appear here from the challenge records.
            </div>
          ) : (
            <div className="border border-white/15 bg-[#0F0F0F]" data-testid="legacy-lb-table">
              {pinned.length > 0 && (
                <ul className="divide-y divide-white/10">{pinned.map((e, i) => <LbRow key={`p${i}`} e={e} rank={0} />)}</ul>
              )}
              {finishers.length > 0 && (
                <>
                  <div className="px-4 sm:px-6 py-2.5 bg-emerald-500/[0.08] border-y border-emerald-500/30 flex items-center gap-2">
                    <Medal size={14} weight="fill" className="text-emerald-400" />
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-300">911 finishers · {finishers.length}</span>
                  </div>
                  <ul className="divide-y divide-white/10">{finishers.map((e, i) => <LbRow key={`f${i}`} e={e} rank={i + 1} />)}</ul>
                </>
              )}
              {working.length > 0 && (
                <>
                  <div className="px-4 sm:px-6 py-2.5 bg-white/[0.03] border-y border-white/10 flex items-center gap-2">
                    <Flame size={14} weight="fill" className="text-[#B32027]" />
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70">Everyone who moved · {working.length}</span>
                  </div>
                  <ul className="divide-y divide-white/10">{orderedWorking.map((e, i) => <LbRow key={`w${i}`} e={e} rank={finishers.length + i + 1} />)}</ul>
                  {working.length > 10 && (
                    <button
                      type="button"
                      data-testid="legacy-lb-toggle"
                      onClick={() => setShowAllLb((v) => !v)}
                      className="w-full py-3 text-[10px] uppercase tracking-[0.3em] font-bold text-white/60 hover:text-[#B32027] border-t border-white/10 inline-flex items-center justify-center gap-2"
                    >
                      {showAllLb ? "Show less" : `Show all ${working.length}`} <CaretDown size={11} weight="bold" className={showAllLb ? "rotate-180" : ""} />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ========================= LEGACY WALL =========================== */}
      <section data-testid="legacy-wall-section" className="px-5 md:px-12 py-16 md:py-24 border-t border-white/10">
        <div className="max-w-6xl">
          <SectionLabel>The Year 1 Legacy Wall</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-3">The faces behind the minutes.</h2>
          <p className="text-white/60 text-base mb-6 max-w-2xl">Every photo, every name, every person we moved for. This is the visual memory book of Year 1 — a wall of moments the community built together.</p>
          {honorees.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-10 text-[11px] uppercase tracking-[0.25em] font-bold" data-testid="legacy-wall-count">
              <span className="text-[#B32027]">{num(honorees.length)} {honorees.length === 1 ? "moment" : "moments"} on the wall</span>
              <span className="text-white/40">Year 1 · 2026 · preserved</span>
            </div>
          )}

          {honorees.length === 0 ? (
            <div className="border border-white/15 bg-[#0F0F0F] px-6 py-14 text-center text-white/45" data-testid="legacy-wall-empty">
              Legacy Wall uploads will appear here from the challenge records.
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-3 [column-fill:_balance]" data-testid="legacy-wall-gallery">
              {honorees.map((h) => (
                <figure key={h.signup_id} data-testid={`legacy-wall-item-${h.signup_id}`} className="mb-3 break-inside-avoid border border-white/10 bg-[#0F0F0F] overflow-hidden group">
                  {h.primary_url && (
                    <div className="overflow-hidden bg-black">
                      <img src={h.primary_url} alt={h.moving_for_name || h.submitter_name} loading="lazy" className="w-full h-auto object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                  )}
                  <figcaption className="p-3">
                    <p className="font-display font-black uppercase tracking-tight text-sm">{h.submitter_name}</p>
                    {h.moving_for_name && (
                      <p className="text-white/55 text-xs mt-0.5">Moving for {h.moving_for_name}{h.moving_for_relationship ? ` · ${h.moving_for_relationship}` : ""}</p>
                    )}
                    {h.family && <span className="inline-block mt-1.5 text-[8px] uppercase tracking-[0.2em] font-bold text-[#B32027] border border-[#B32027]/50 px-1.5 py-0.5">Family</span>}
                    {h.caption && <p className="text-white/50 text-xs italic mt-2 leading-relaxed">&ldquo;{h.caption}&rdquo;</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===================== IN THE NEWS / STORIES ===================== */}
      <section data-testid="legacy-news" className="px-5 md:px-12 py-16 md:py-24 border-t border-white/10 bg-[#0C0C0C]">
        <div className="max-w-4xl">
          <SectionLabel>Sweat With Slay in the news</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-3">Year 1 made headlines.</h2>
          <p className="text-white/60 text-base mb-10 max-w-2xl flex items-center gap-2"><Newspaper size={16} weight="fill" className="text-[#B32027]" /> The coverage and stories that carried the challenge beyond our own circle.</p>

          {FEATURED_VIDEO && (
            <div className="border border-white/10 bg-[#0F0F0F] p-5 md:p-7 mb-10" data-testid="legacy-video">
              <p className="text-[#B32027] uppercase tracking-[0.3em] text-[10px] font-bold mb-4">/ Year 1 in motion</p>
              <PressVideoFeature item={FEATURED_VIDEO} />
            </div>
          )}

          <div className="space-y-8">
            {PRESS.filter((p) => p.id !== FEATURED_VIDEO?.id).map((item) => (
              <PressCard key={item.id} item={item} testId={`legacy-press-${item.id}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ========================= HIGHLIGHTS ============================ */}
      <section data-testid="legacy-highlights" className="px-5 md:px-12 py-16 md:py-24 border-t border-white/10">
        <div className="max-w-3xl">
          <SectionLabel>Year 1 highlights</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-10">How it unfolded.</h2>
          <div>
            <Highlight date="The spark" title="Sweat With Slay becomes Sweat for Sacrifice" body="One idea — 911 minutes of movement for the ones we carry — turned a fitness brand into a mission." />
            <Highlight date="September 2026" title="The challenge went live" body="Participants signed up from across the country and started logging minutes, dedicating every session to someone who mattered." />
            <Highlight date="Mid-challenge" title="The leaderboard caught fire" body="Daily standings turned movement into friendly competition. The KMS crew pushed each other all month." />
            <Highlight date="September 5, 2026" title="News 12 Long Island told the story" body="The segment put Uncle Timmy and Uncle Harry's names in front of a whole region." />
            <Highlight date="The stretch" title="First finishers crossed 911" body="One by one, participants completed all 911 minutes and earned the permanent 911 Finisher badge." />
            <Highlight date="The finish" title="We hit our fundraising goal for the families" body="Every dollar split 50/50 between Tunnel to Towers and the FDNY Fire Family Transport Foundation." />
            <Highlight title="Year 1: complete" body="The first chapter is officially in the books." />
          </div>
        </div>
      </section>

      {/* ========================= THE CAUSE ============================= */}
      <section data-testid="legacy-cause" className="px-5 md:px-12 py-16 md:py-24 border-t border-white/10 bg-[#0C0C0C]">
        <div className="max-w-4xl">
          <SectionLabel>The cause</SectionLabel>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-4">We moved for something bigger.</h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl">Every direct donation and every dollar of net merch profit was split evenly between two organizations at the heart of this challenge.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-white/12 bg-[#0F0F0F] p-7">
              <h3 className="font-display font-black uppercase tracking-tight text-2xl mb-2">Tunnel to Towers</h3>
              <p className="text-white/60 text-sm leading-relaxed">Supporting first responders, service members and their families — the same people who ran toward the danger our uncles ran toward on 9/11.</p>
            </div>
            <div className="border border-white/12 bg-[#0F0F0F] p-7">
              <h3 className="font-display font-black uppercase tracking-tight text-2xl mb-2">FDNY Fire Family Transport</h3>
              <p className="text-white/60 text-sm leading-relaxed">Honoring the fallen and standing with the families they left behind — carrying the weight so they don&rsquo;t carry it alone.</p>
            </div>
          </div>
          <div className="border border-[#B32027]/60 bg-[#B32027]/[0.06] p-7 mt-4 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027] mb-2">Final Year 1 impact</p>
            <p className="font-display font-black tracking-tighter text-5xl md:text-6xl">{money(totalRaised)}</p>
            <p className="text-white/60 text-sm mt-2">raised together · split 50/50 between Tunnel to Towers & FDNY Fire Family Transport</p>
          </div>
        </div>
      </section>

      {/* ========================= THANK YOU ============================= */}
      <section data-testid="legacy-thankyou" className="px-5 md:px-12 py-20 md:py-28 border-t border-white/10">
        <div className="max-w-3xl">
          <h2 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl mb-8 leading-[0.85]">We did that.</h2>
          <div className="text-white/80 text-lg leading-relaxed space-y-4">
            <p>Year 1 started as an idea. Then people showed up.</p>
            <p>You ran. You walked. You lifted. You climbed. You donated. You shared. You competed. You encouraged each other.</p>
            <p>And together we turned Sweat With Slay into something real — a movement with a name, a cause, and a whole community behind it.</p>
            <p className="text-white font-bold">To every participant, donor, supporter, friend and family member who carried a name this year: thank you. This one&rsquo;s yours.</p>
          </div>
        </div>
      </section>

      {/* ======================= CLOSE OUT / YEAR 2 ====================== */}
      <section data-testid="legacy-closeout" className="px-5 md:px-12 py-16 md:py-24 border-t border-white/10 bg-[#0C0C0C]">
        <div className="max-w-3xl text-center mx-auto">
          <div className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 px-4 py-2 mb-6">
            <CheckCircle size={16} weight="fill" className="text-emerald-400" />
            <span className="text-sm uppercase tracking-[0.3em] font-bold text-emerald-300">Year 1: Complete</span>
          </div>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl mb-4">The first chapter<br />is in the books.</h2>
          <p className="text-white/60 text-lg mb-10">Stay tuned. Year 2 is only the beginning.</p>

          <form onSubmit={joinYear2} data-testid="legacy-year2-form" className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              data-testid="legacy-year2-email"
              className="flex-1 bg-[#0F0F0F] border-2 border-white/20 focus:border-[#B32027] outline-none px-4 py-3.5 text-white placeholder-white/40"
            />
            <button
              type="submit"
              data-testid="legacy-year2-submit"
              className="bg-[#B32027] hover:bg-[#D42730] text-white px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.25em] whitespace-nowrap inline-flex items-center justify-center gap-2"
            >
              Be first to know <ArrowUpRight size={13} weight="bold" />
            </button>
          </form>
          {signupMsg && <p data-testid="legacy-year2-msg" className="text-emerald-300 text-sm mt-4">{signupMsg}</p>}
          <p className="text-white/35 text-xs mt-3">A heads-up for Year 2 — no spam, no automated challenge emails.</p>
        </div>
      </section>

      <FlagStripe position="bottom" />
      <Footer />
    </main>
  );
}
